import { buildGroupsMulti } from './query.js';
import { reorderRows, mergeViewOrder } from './grid-util.js';
export class GridRowDrag {
    // ═══════════════════════ 행 드래그 ═══════════════════════
    /** 끌거나 보낼 행: row 가 체크돼 있으면 체크 행, 선택 범위 안이면 범위 행, 아니면 그 행 — 화면 순서대로 */
    /** @internal */ dragRowsFor(row) {
        const rr = this.getRangeRows();
        const rows = this.checked.has(row) ? this.getCheckedRows() : rr.length > 1 && rr.includes(row) ? rr : [row];
        const pos = new Map();
        this.disp.forEach((r, i) => { if (r >= 0)
            pos.set(r, i); });
        return rows.sort((a, b) => (pos.get(a) ?? 0) - (pos.get(b) ?? 0));
    }
    /** @internal */ startRowDrag(row) {
        const rows = this.dragRowsFor(row);
        this.drag = { rows, target: -1, pos: null };
        this.ghost.style.position = 'fixed'; // 다른 그리드 위로도 따라가게
        this.ghost.textContent = rows.length > 1 ? `${rows.length}행 이동` : this.cellText(row, this.treeLabelCol() || this.cols.find(c => !c.special).id) || '1행 이동';
        const move = (ev) => this.onDragMove(ev);
        const up = (ev) => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); this.onDragEnd(ev); };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
        this.root.classList.add('og-dragging');
    }
    /** @internal */ isDescendant(row, ofRows) {
        const t = this.opts.tree;
        if (!t)
            return ofRows.includes(row);
        const ids = new Set(ofRows.map(r => String(this.ds.get(r, t.idCol))));
        const byId = new Map();
        for (const r of this.view)
            byId.set(String(this.ds.get(r, t.idCol)), r);
        let x = row, guard = 0;
        while (x != null && x >= 0 && guard++ < 64) {
            const id = String(this.ds.get(x, t.idCol));
            if (ids.has(id))
                return true;
            const p = this.ds.get(x, t.parentCol);
            x = p == null ? undefined : byId.get(String(p));
        }
        return false;
    }
    /** @internal */ onDragMove(e) {
        const d = this.drag;
        const rootBox = this.root.getBoundingClientRect();
        this.ghost.hidden = false;
        this.ghost.style.left = e.clientX + 12 + 'px';
        this.ghost.style.top = e.clientY + 8 + 'px';
        // 다른 그리드 위: 그 그리드가 받는지 보고 그쪽에 표시
        const over = document.elementFromPoint(e.clientX, e.clientY)?.closest('.og');
        const og = over?.__onegrid ?? null;
        if (og && og !== this) {
            this.dropLine.hidden = true;
            d.target = -1;
            if (d.ext && d.ext !== og)
                d.ext.hideExtDrop();
            const acc = !!this.opts.dragSource && !!og.opts.dropTarget?.accept.includes(this.opts.dragSource.group);
            d.ext = acc ? og : null;
            if (acc) {
                const r = og.showExtDrop(e);
                d.extTarget = r.target;
                d.extPos = r.pos;
            }
            this.root.classList.toggle('og-nodrop', !acc);
            return;
        }
        if (d.ext) {
            d.ext.hideExtDrop();
            d.ext = null;
        }
        this.root.classList.remove('og-nodrop');
        if (!this.opts.rowDrag) {
            d.target = -1;
            this.dropLine.hidden = true;
            return;
        }
        // 가장자리 자동 스크롤
        const main = this.bodyPanes[1], mb = main.getBoundingClientRect();
        if (e.clientY < mb.top + 24)
            main.scrollTop -= 20;
        else if (e.clientY > mb.bottom - 24)
            main.scrollTop += 20;
        const el = document.elementFromPoint(e.clientX, e.clientY);
        const rowEl = el?.closest('.og-r');
        if (!rowEl || rowEl.__slot == null || !this.root.contains(rowEl)) {
            d.target = -1;
            this.dropLine.hidden = true;
            return;
        }
        const vi = this.first + rowEl.__slot;
        const target = this.disp[vi];
        if (target == null || target < 0) {
            d.target = -1;
            this.dropLine.hidden = true;
            return;
        }
        const rb = rowEl.getBoundingClientRect();
        const f = (e.clientY - rb.top) / rb.height;
        const pos = this.opts.tree ? (f < 0.28 ? 'before' : f > 0.72 ? 'after' : 'inside') : f < 0.5 ? 'before' : 'after';
        const invalid = this.isDescendant(target, d.rows);
        d.target = invalid ? -1 : target;
        d.pos = pos;
        this.dropLine.hidden = invalid;
        const top = rb.top - rootBox.top;
        if (pos === 'inside') {
            this.dropLine.className = 'og-dropline og-dropin';
            this.dropLine.style.top = top + 'px';
            this.dropLine.style.height = rb.height + 'px';
        }
        else {
            this.dropLine.className = 'og-dropline';
            this.dropLine.style.top = top + (pos === 'after' ? rb.height : 0) - 1 + 'px';
            this.dropLine.style.height = '';
        }
        const lv = this.opts.tree ? (this.treeLevel.get(target) ?? 0) + (pos === 'inside' ? 1 : 0) : 0;
        this.dropLine.style.left = this.paneWidth[0] ? lv * 16 + 'px' : '0px';
    }
    /** @internal */ onDragEnd(_e) {
        const d = this.drag;
        this.drag = null;
        this.dropLine.hidden = this.ghost.hidden = true;
        this.ghost.style.position = '';
        this.root.classList.remove('og-dragging', 'og-nodrop');
        if (d?.ext) {
            const og = d.ext;
            og.hideExtDrop();
            const src = this.opts.dragSource;
            const data = src.toData ? src.toData(d.rows, this) : this.ds.toObjects(d.rows);
            og.undoBatch(this.t('끌어 놓기'), () => og.opts.dropTarget.onDrop({ data, target: d.extTarget ?? -1, position: d.extPos ?? 'after', sourceGrid: this, grid: og }));
            return;
        }
        if (!d || d.target < 0 || !d.pos || d.rows.includes(d.target))
            return;
        this.dropRows(d.rows, d.target, d.pos);
    }
    /** 이 그리드의 행을 받을 수 있는 다른 그리드 (dragSource.group 을 dropTarget.accept 에 둔 것) — 화면 순서로 이 그리드 다음부터 */
    dropTargets() {
        const g = this.opts.dragSource?.group;
        if (!g || typeof document === 'undefined')
            return [];
        const all = [...document.querySelectorAll('.og')].map(el => el.__onegrid).filter((o) => !!o);
        const me = all.indexOf(this), order = me < 0 ? all : [...all.slice(me + 1), ...all.slice(0, me)];
        return order.filter(o => o !== this && !!o.opts.dropTarget?.accept.includes(g));
    }
    /**
     * 다른 그리드로 보내기 — 끌어 놓기의 키보드·메뉴 대안 (Alt+Shift+→, 셀 메뉴 "다른 그리드로 보내기").
     * 보낼 행: 체크 행 > 선택 범위 행 > 포커스 행. 대상 생략 = dropTargets()[0].
     * 놓을 자리(at) 생략 = 대상 그리드의 포커스 행 뒤(없으면 끝, target -1). 끌어 놓기와 같은 dropTarget.onDrop 이벤트(실행 취소 한 단위). 보냈으면 true
     */
    sendRows(target, opts = {}) {
        const src = this.opts.dragSource, og = target ?? this.dropTargets()[0];
        if (!src || !og || og === this || !og.opts.dropTarget?.accept.includes(src.group))
            return false;
        this.commitEdit();
        const fr = this.getFocusedRow();
        const rows = opts.rows ?? (this.checked.size ? this.getCheckedRows() : fr >= 0 ? this.dragRowsFor(fr) : []);
        if (!rows.length)
            return false;
        const data = src.toData ? src.toData(rows, this) : this.ds.toObjects(rows);
        const at = opts.at ?? og.getFocusedRow();
        og.undoBatch(this.t('끌어 놓기'), () => og.opts.dropTarget.onDrop({ data, target: at ?? -1, position: opts.position ?? 'after', sourceGrid: this, grid: og }));
        const name = og.root.getAttribute('aria-label') ?? '';
        this.live.textContent = `${rows.length}${this.t('행')} → ${name}`;
        return true;
    }
    /**
     * 행 여러 개 추가 후 보기 순서에서 target 행 앞/뒤에 끼움 (다른 그리드에서 놓은 자리에 넣기). target -1 = 끝.
     * 정렬·그룹·트리 중이면 끝에 (보기 순서를 정할 수 없음). 추가한 물리 행 번호 반환 — 실행 취소 한 단위
     */
    insertRows(values, target = -1, position = 'after') {
        this.commitEdit();
        if (!values.length || this.protectBlocks('insert'))
            return [];
        const rows = this.undoBatch(this.t('행 추가'), () => { const out = []; this.ds.batch(() => { for (const v of values) {
            const iv = this.beforeRowInsert(this.view.length + out.length, { ...(this.opts.newRow?.() ?? {}), ...v }, 'drop');
            if (iv)
                out.push(this.ds.addRow(iv));
        } }); return out; });
        if (target >= 0 && !this.opts.tree && !this.isGrouped() && !this.extOrder && !this.srm && !this.sortKeys.length && this.ds.isVisibleRow(target))
            this.setViewOrder(reorderRows(this.view, rows, target, position === 'before' ? 'before' : 'after'));
        return rows;
    }
    /** 다른 그리드에서 끌어 오는 중: 놓을 위치 표시 (행 사이 선, 트리면 폴더 안, 빈 곳이면 본문 테두리) */
    showExtDrop(e) {
        const rootBox = this.root.getBoundingClientRect();
        const rowEl = document.elementFromPoint(e.clientX, e.clientY)?.closest('.og-r');
        const vi = rowEl && rowEl.__slot != null && this.root.contains(rowEl) ? this.first + rowEl.__slot : -1;
        const target = vi >= 0 ? this.disp[vi] : -1;
        this.root.classList.add('og-dropover');
        if (target == null || target < 0) { // 빈 그리드·빈 곳 → 끝에 추가
            const mb = this.bodyPanes[1].getBoundingClientRect();
            this.dropLine.hidden = false;
            this.dropLine.className = 'og-dropline og-dropin og-dropall';
            this.dropLine.style.left = '0px';
            this.dropLine.style.top = mb.top - rootBox.top + 'px';
            this.dropLine.style.height = mb.height + 'px';
            return { target: -1, pos: 'after' };
        }
        const rb = rowEl.getBoundingClientRect(), f = (e.clientY - rb.top) / rb.height;
        const pos = this.opts.tree ? (f < 0.28 ? 'before' : f > 0.72 ? 'after' : 'inside') : f < 0.5 ? 'before' : 'after';
        const top = rb.top - rootBox.top;
        this.dropLine.hidden = false;
        if (pos === 'inside') {
            this.dropLine.className = 'og-dropline og-dropin';
            this.dropLine.style.top = top + 'px';
            this.dropLine.style.height = rb.height + 'px';
        }
        else {
            this.dropLine.className = 'og-dropline';
            this.dropLine.style.top = top + (pos === 'after' ? rb.height : 0) - 1 + 'px';
            this.dropLine.style.height = '';
        }
        this.dropLine.style.left = '0px';
        return { target, pos };
    }
    hideExtDrop() { this.dropLine.hidden = true; this.root.classList.remove('og-dropover'); }
    /** 행 이동 (드래그와 같은 처리를 코드로도 호출 가능) */
    /**
     * 선택 범위의 행을 dir(−1 위, +1 아래)로 한 칸 옮김. 실행 취소 가능(보기 순서 + orderCol 값).
     * 정렬 중: 지금 보이는 순서를 그대로 두고 정렬 표시를 지운 뒤 옮김. 그룹: 같은 그룹 안에서만 (머리행·푸터를 넘지 않음).
     * 트리: 같은 부모의 형제 사이에서만 — tree.orderCol 이 있어야 함 (형제 순서를 저장할 곳)
     */
    moveSelectedRows(dir) {
        if (this.srm || this.extOrder)
            return false;
        const { r0, r1, c0, c1 } = this.range();
        const rows = [];
        for (let vi = r0; vi <= r1; vi++) {
            const r = this.disp[vi];
            if (r >= 0)
                rows.push(r);
            else if (this.isGrouped())
                return false;
        }
        if (!rows.length)
            return false;
        let target, t = this.opts.tree;
        if (t) {
            if (!t.orderCol)
                return false;
            const par = (r) => String(this.ds.get(r, t.parentCol) ?? '');
            if (rows.some(r => par(r) !== par(rows[0])))
                return false;
            const sib = Array.from(this.disp).filter(r => r >= 0 && par(r) === par(rows[0]));
            const a = sib.indexOf(rows[0]), b = sib.indexOf(rows[rows.length - 1]);
            if (a < 0 || b - a !== rows.length - 1)
                return false;
            target = sib[dir < 0 ? a - 1 : b + 1] ?? -1;
        }
        else {
            const tv = dir < 0 ? r0 - 1 : r1 + 1;
            target = tv >= 0 && tv < this.disp.length ? this.disp[tv] : -1;
        }
        if (target < 0)
            return false;
        if (this.sortKeys.length) {
            this.sortKeys = [];
            this.renderHeader();
            this.notifyLayout();
        } // 보이는 순서를 그대로 기준으로
        const fci = this.focus.ci;
        this.dropRows(rows, target, dir < 0 ? 'before' : 'after');
        const a = this.disp.indexOf(rows[0]), b = this.disp.indexOf(rows[rows.length - 1]);
        if (a >= 0 && b >= 0) {
            this.setFocus(a, fci === c0 ? c1 : c0, false);
            this.setFocus(b, fci, true);
        }
        return true;
    }
    /** 보기 순서 되돌리기 (행 이동 실행 취소): 지금 보이는 행만, 없어진 행은 빼고 새 행은 뒤에 */
    /** @internal */ restoreViewOrder(order) {
        if (this.opts.tree || this.extOrder || this.srm)
            return;
        this.setViewOrder(mergeViewOrder(order, this.view));
    }
    /** @internal */ setViewOrder(v) {
        const nv = Uint32Array.from(v);
        this.view = nv;
        if (this.isGrouped() && this.groupArgs) {
            const r = buildGroupsMulti(this.ds, nv, this.groupArgs.levels, path => this.groupCollapseAll !== this.groupToggled.has(path), this.groupArgs.order);
            this.disp = r.disp;
            this.groups = r.groups;
        }
        else
            this.disp = new Int32Array(nv.buffer);
        this.afterView();
    }
    dropRows(rows, target, pos) {
        if (this.opts.onRowDrop?.({ rows, target, position: pos, grid: this }) === false)
            return;
        const t = this.opts.tree;
        const undo = [];
        const set = (r, col, v) => { const old = this.ds.get(r, col); if (this.ds.set(r, col, v))
            undo.push({ row: r, col, old }); };
        if (t) {
            const newParent = pos === 'inside' ? this.ds.get(target, t.idCol) : this.ds.get(target, t.parentCol);
            const samePar = (r) => String(this.ds.get(r, t.parentCol) ?? '') === String(newParent ?? '');
            // 새 부모의 현재 자식들(화면 순서)에서 옮길 행을 빼고 끼워 넣는다
            const siblings = Array.from(this.view).filter(r => samePar(r) && !rows.includes(r));
            let at = pos === 'inside' ? siblings.length : siblings.indexOf(target) + (pos === 'after' ? 1 : 0);
            if (at < 0)
                at = siblings.length;
            siblings.splice(at, 0, ...rows);
            this.ds.batch(() => {
                for (const r of rows)
                    set(r, t.parentCol, newParent);
                if (t.orderCol)
                    siblings.forEach((r, i) => set(r, t.orderCol, (i + 1) * 10));
            });
            if (pos === 'inside')
                this.treeCollapsed.delete(String(newParent));
            this.rebuild();
        }
        else {
            const order = Array.from(this.view), v = reorderRows(order, rows, target, pos === 'before' ? 'before' : 'after');
            if (this.opts.orderCol)
                this.ds.batch(() => v.forEach((r, i) => set(r, this.opts.orderCol, i + 1)));
            this.setViewOrder(v);
            this.pushUndo({ label: this.t('행 이동'), at: Date.now(), cells: undo, rows: [], order }); // orderCol 이 없어도 보기 순서로 되돌림
            const vi = this.disp.indexOf(rows[0]);
            if (vi >= 0)
                this.setFocus(vi, this.focus.ci, false);
            return;
        }
        this.pushUndo({ label: this.t('행 이동'), at: Date.now(), cells: undo, rows: [] });
        const vi = this.disp.indexOf(rows[0]);
        if (vi >= 0)
            this.setFocus(vi, this.focus.ci, false);
    }
}
